<script setup lang="ts">
import SnackBar from '@/components/base/SnackBar.vue'
import { defineAsyncComponent, onBeforeMount, ref } from 'vue'
import { useRouter } from 'vue-router'
import type { User as OidcUser } from 'oidc-client-ts'
import { initAuth, userManager, forceRelogin } from '@/services/auth/oidc'
import { useAuthStore } from './stores/auth'
import type { User } from '@/types/auth'
import { syncAuth } from './services/api/auth'
import { ConfigProvider } from 'ant-design-vue'
import { antdThemeConfig } from '@/config/antd-theme'
import { initTaskSocket } from '@/composables/useTaskSocket'
import { useUnsavedProjectGuard } from '@/composables/useUnsavedProjectGuard'
import { installShortcutDispatcher } from '@/composables/shortcuts'
import config from '@/config'

const ChatBot = defineAsyncComponent(() => import('@/views/chat-bot/ChatBot.vue'))

const init = ref(false)
const authError = ref(false)
const authStore = useAuthStore()
const router = useRouter()
useUnsavedProjectGuard()
installShortcutDispatcher()

const mapProfile = (user: OidcUser): User => ({
  sub: user.profile.sub,
  email: user.profile.email ?? '',
  email_verified: user.profile.email_verified,
  name: user.profile.name,
  preferred_username: user.profile.preferred_username,
  given_name: user.profile.given_name,
  family_name: user.profile.family_name,
  picture: user.profile.picture,
})

const handleLogin = async () => {
  if (config.devSkipAuthForDevRoutes && window.location.pathname.startsWith('/dev/')) {
    console.warn('VITE_DEV_SKIP_AUTH: Keycloak login skipped for a /dev/* route. Dev-only.')
    init.value = true
    return
  }
  try {
    const user = await initAuth()
    authStore.setCurrentUser(mapProfile(user))
    userManager.events.addUserLoaded((u) => authStore.setCurrentUser(mapProfile(u)))

    // The OIDC callback restores the pre-login URL with history.replaceState,
    // which vue-router doesn't observe — its initial route is still the
    // callback URL ('/'). Re-sync so deep links survive the login round-trip.
    await router.replace(
      window.location.pathname + window.location.search + window.location.hash,
    )

    try {
      await syncAuth()
    } catch (error) {
      console.warn('User sync failed (backend may be unavailable):', error)
    }

    init.value = true
    if (config.websocketEnabled) initTaskSocket()
  } catch (error) {
    console.error('Sign-in failed:', error)
    authError.value = true
  }
}
onBeforeMount(() => {
  handleLogin()
})
</script>

<template>
  <ConfigProvider :theme="antdThemeConfig">
    <template v-if="init">
      <RouterView />
      <SnackBar />
      <ChatBot />
    </template>
    <div v-else-if="authError" class="auth-error">
      <p>Sign-in failed. Please try again.</p>
      <v-btn color="primary" @click="forceRelogin()">Retry</v-btn>
    </div>
  </ConfigProvider>
</template>

<style scoped>
.auth-error {
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
}
</style>
